import axios from '../../axios';

export default {
  namespaced: true,

  state: {
    /* blog articles */
    allBlogArticlePages: null,
    currentBlogArticlePage: null,
    currentBlogArticleId: null,

    /* blog tags + categories */
    allBlogTags: null,
    currentBlogTag: null,

    /* blog regions */
    allBlogRegions: null,
  },

  getters: {
    /* blog articles */
    getAllBlogArticlePages: (state) => {
      if (state.allBlogArticlePages) {
        return state.allBlogArticlePages;
      }
    },
    getAllBlogArticlePagesByTagId: (state, getters) => (tagId) => {
      if (state.allBlogArticlePages) {
        // returns all blogs where it is tagged with given tagId
        const blogsByTag = state.allBlogArticlePages.filter((blog) => blog.tags && blog.tags.findIndex((tag) => tag.id === tagId) > -1);
        return blogsByTag;
      }
    },
    getAllBlogArticlePagesByCategoryId: (state, getters) => (categoryId) => {
      if (state.allBlogArticlePages) {
        // returns all blogs by categoryid
        const blogsByCategory = state.allBlogArticlePages.filter((blog) => blog.categoryId === categoryId);
        return blogsByCategory;
      }
    },
    getAllBlogArticlePagesByRegionId: (state, getters) => (regionId) => {
      if (state.allBlogArticlePages) {
        // returns all blogs by regionId
        const blogsByRegion = state.allBlogArticlePages.filter((blog) => blog.regionId === regionId);
        return blogsByRegion;
      }
    },
    getBlogArticlePageByURL: (state, getters) => (url) => {
      if (state.allBlogArticlePages) {
        return state.allBlogArticlePages.find((l) => l.cmsRelativeURL === url);
      }
    },
    getCurrentBlogArticlePage: (state) => {
      if (state.currentBlogArticlePage) {
        return state.currentBlogArticlePage;
      }
    },
    getCurrentBlogArticleId: (state) => {
      if (state.currentBlogArticleId) {
        return state.currentBlogArticleId;
      }
    },

    /* random - returns 2 blog articles from same category + 2 articles from same region */
    getRandomBlogArticlesByBlogId: (state, getters) => (blogId) => {
      const randomBlogsArticles = [];
      if (state.allBlogArticlePages) {
        const blog = state.allBlogArticlePages.find((b) => b.id === blogId);
        if (blog) {
          // add 2 random blog articles from same category
          const allBlogsByCat = getters.getAllBlogArticlePagesByCategoryId(blog.categoryId);
          if (allBlogsByCat) {
            // Return 3 to avoid duplicating current article
            getters.getRandom(allBlogsByCat, 3).forEach((b) => {
              if (randomBlogsArticles.length < 2 && b.id != blog.id) {
                randomBlogsArticles.push(b);
              }
            });
          }
          // add 2 random blog articles from same region
          const allBlogsByRegion = getters.getAllBlogArticlePagesByRegionId(blog.regionId);
          if (allBlogsByRegion) {
            // Return 5 to avoid duplicating current article and category articles
            getters.getRandom(allBlogsByRegion, 5).forEach((b) => {
              if (randomBlogsArticles.length < 4 && b.id != blog.id && randomBlogsArticles.indexOf(b) < 0) {
                randomBlogsArticles.push(b);
              }
            });
          }
        }
      }
      return randomBlogsArticles;
    },
    getRandom: (state, getters) => (blogs, maxNumber) => {
      const randomBlogs = [];
      if (blogs) {
        if (blogs.length > maxNumber) {
          const arr = [];
          while (arr.length < maxNumber) {
            const r = Math.floor(Math.random() * (blogs.length - 1));
            if (arr.indexOf(r) === -1) {
              arr.push(r);
              randomBlogs.push(blogs[r]); // push random blog onto array
            }
          }
        } else {
          blogs.forEach((b) => { randomBlogs.push(b); }); // if number of blogs is smaller than maxNumber then return all
        }
      }
      return randomBlogs;
    },
    /* blog tags */
    getAllBlogTags: (state) => {
      if (state.allBlogTags) {
        return state.allBlogTags.filter((tag) => tag.isBlogCategory === false);
      }
    },

    /* blog categories */
    getAllBlogCategories: (state) => {
      if (state.allBlogTags) {
        return state.allBlogTags.filter((tag) => tag.isBlogCategory === true);
      }
    },
    getBlogHomeMainCategories: (state, getters) => {
      // returns 2 blog categories (marked as 'BlogHomeCat1' + marked as 'BlogHomeCat2')
      const mainCats = [];
      if (getters.getAllBlogCategories) {
        const mainCat1 = getters.getAllBlogCategories.find((c) => c.blogHomeCat1 === true);
        const mainCat2 = getters.getAllBlogCategories.find((c) => c.blogHomeCat2 === true);

        if (mainCat1) {
          mainCats.push(mainCat1);
        }
        if (mainCat2) {
          mainCats.push(mainCat2);
        }
      }
      return mainCats;
    },

    /* blog categories + tags */
    getBlogTagById: (state, getters) => (id) => {
      if (state.allBlogTags) {
        return state.allBlogTags.find((l) => l.id === id);
      }
    },
    getCurrentBlogTag: (state) => {
      if (state.currentBlogTag) {
        return state.currentBlogTag;
      }
    },
    getBlogTagByURL: (state, getters) => (url) => {
      if (state.allBlogTags) {
        return state.allBlogTags.find((l) => l.blogTagCMSRelativeURL === url);
      }
    },

    /* blog regions */
    getAllBlogRegions: (state) => {
      if (state.allBlogRegions) {
        return state.allBlogRegions;
      }
    },
    getBlogRegionById: (state, getters) => (id) => {
      if (state.allBlogRegions) {
        return state.allBlogRegions.find((r) => r.id === id);
      }
    },
  },

  mutations: {
    /* blog articles */
    setAllBlogArticlePages(state, blogArticlePages) {
      state.allBlogArticlePages = blogArticlePages;
    },
    setCurrentBlogArticlePage(state, blogArticlePage) {
      state.currentBlogArticlePage = blogArticlePage;
    },
    setCurrentBlogArticleId(state, blogArticleId) {
      state.currentBlogArticleId = blogArticleId;
    },
    /* blog tags + categories */
    setAllBlogTags(state, blogTags) {
      state.allBlogTags = blogTags;
    },
    setCurrentBlogTag(state, blogTag) {
      state.currentBlogTag = blogTag;
    },
    /* Blog Regions */
    setAllBlogRegions(state, blogRegions) {
      state.allBlogRegions = blogRegions;
    },
  },

  actions: {
    /* blog articles */
    // fetchAllBlogArticlePages({
    //  commit, dispatch, getters, rootGetters,
    // }) {
    //  return new Promise((resolve, reject) => {
    //    const odata = '$select=id,name,imagePath,thumbImagePath,thumbImageAltText,regionId,CMSRelativeURL,featuredBlog,categoryId&$expand=languageSpecificBlogs($select=title,introtext)&$expand=tags($select=id,name)';

    //    axios.get(`/core/blogpages/published?${odata}`)
    //      .then((res) => {
    //        commit('setAllBlogArticlePages', res.data);
    //        resolve(res.data);
    //      })
    //      .catch((error) => console.log(error));
    //  });
    // },
    fetchBlogArticlePageById({
      commit, state, dispatch, getters, rootGetters,
    }, id) {
      return new Promise((resolve, reject) => {
        state.currentBlogArticlePage = null;
        // blog Article page not in allBlogPages state so fetch and set from API
        const odata = '$select=id,name,imagePath,heroBlog,imageAltTag,regionId,numberOfViews,sortOrder,status,imageLabelColor,slideShowId,CMSRelativeURL,writtenByName,lastEditDate,featuredBlog1,featuredBlog2,writtenByUserId,writtenByUserPic,categoryId,readTime,expiryDateTime,expired,thumbImagePath,thumbImageAltText&$expand=languageSpecificBlogs($select=content,title,introtext,languageId,metaDescription,metaKeywords,structuredSchema,AnchorMenuContent,Methodology)&$expand=tags($select=id,name,sortSeq)';

        axios.get(`/core/blogpages/published/${id}?${odata}`)
          .then((res) => {
            commit('setCurrentBlogArticlePage', res.data);
            dispatch('pages/setPreRenderDone', null, { root: true });
            resolve(res.data);
          });
      });
    },
    /* blog tags + categories */
    // fetchAllBlogTags({
    //  commit, dispatch, getters, rootGetters,
    // }) {
    //  return new Promise((resolve, reject) => {
    //    const odata = '$select=id,name,imagePath,image2Path,pageTitle,blogTagCMSRelativeURL,featuredBlogId,isBlogCategory,imageAltTag,image2AltTag,blogHomeCat1,blogHomeCat2&$expand=languageSpecificTags($select=description,introTitle,heading,headingText,languageId,metaDescription,metaKeywords,structuredSchema)';

    //    axios.get(`/core/blogtags/published?${odata}`)
    //      .then((res) => {
    //        commit('setAllBlogTags', res.data);
    //        resolve(res.data);
    //      })
    //      .catch((error) => console.log(error));
    //  });
    // },
    fetchBlogTagById({
      commit, state, dispatch, getters, rootGetters,
    }, id) {
      return new Promise((resolve, reject) => {
        state.currentBlogTag = null;

        // first check if blog tag for this id exist in state already
        if (state.allBlogTags && state.allBlogTags.find((l) => l.id === id)) {
          commit('setCurrentBlogTag', { ...state.allBlogTags.find((l) => l.id === id) }); // we want to copy values not have a reference to object in allBlogTags
          resolve();
        } else {
          // blog tag not in allBlogTags state so fetch and set from API
          const odata = '$select=id,name,sortSeq,imagePath,image2Path,pageTitle,blogTagCMSRelativeURL,featuredBlogId,isBlogCategory,imageAltTag,image2AltTag,blogHomeCat1,blogHomeCat2&$expand=languageSpecificTags($select=description,introTitle,heading,headingText,languageId,metaDescription,metaKeywords,structuredSchema)';

          axios.get(`/core/blogtags/published/${id}?${odata}`)
            .then((res) => {
              commit('setCurrentBlogTag', res.data);
              resolve(res.data);
            });
        }
      });
    },

    /* Blog Regions */
    // fetchAllBlogRegions({ commit, dispatch, getters }) {
    //  axios.get('/core/blogregions/all?$select=id,name')
    //    .then((res) => {
    //      commit('setAllBlogRegions', res.data);
    //    })
    //    .catch((error) => console.log(error));
    // },

  },
};
